/**
 * ProductDetailModal - View/Edit modal for products
 * Shows all product fields including variants, pricing, and vendor links
 * Phase 3: Added vendor item linking/unlinking functionality
 * Phase 5: Added price recalculation functionality
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { 
  Loader2, 
  Package, 
  DollarSign, 
  Layers, 
  Link as LinkIcon,
  Unlink,
  Save,
  X,
  Tag,
  Truck,
  Info,
  Search,
  Check,
  ArrowRight,
  TrendingUp,
  Calculator,
  RefreshCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pricing type badges
const PRICING_TYPE_BADGE = {
  matrix: { label: 'Matrix', color: 'bg-blue-500' },
  per_sqft: { label: 'Per Sq Ft', color: 'bg-green-500' },
  option_matrix: { label: 'Options', color: 'bg-purple-500' },
  fixed: { label: 'Fixed', color: 'bg-gray-500' },
  tiered: { label: 'Tiered', color: 'bg-amber-500' }
};

const ProductDetailModal = ({ open, onOpenChange, productId, onUpdate }) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [vendorItem, setVendorItem] = useState(null);
  
  // Vendor linking state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [vendorItems, setVendorItems] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [loadingVendorItems, setLoadingVendorItems] = useState(false);
  const [selectedVendorItem, setSelectedVendorItem] = useState(null);
  const [linking, setLinking] = useState(false);
  
  // Pricing state
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    if (open && productId) {
      loadProduct();
    }
  }, [open, productId]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/products/${productId}`);
      if (res.ok) {
        const data = await res.json();
        setProduct(data);
        setFormData({
          name: data.name || '',
          sku: data.sku || '',
          description: data.description || '',
          retail_price: data.retail_price || data.base_price || 0,
          is_active: data.is_active !== false,
          taxable: data.taxable !== false,
        });

        // Load linked vendor item if exists
        if (data.preferred_vendor_item_id) {
          const viRes = await fetch(`${API_URL}/api/catalog/vendor-items/${data.preferred_vendor_item_id}`);
          if (viRes.ok) {
            setVendorItem(await viRes.json());
          }
        } else {
          setVendorItem(null);
        }
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Failed to load product');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success('Product updated');
        setEditMode(false);
        loadProduct();
        onUpdate?.();
      } else {
        toast.error('Failed to update product');
      }
    } catch (error) {
      toast.error('Error saving product');
    }
    setSaving(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Load vendor items for linking
  const loadVendorItems = async (search = '') => {
    setLoadingVendorItems(true);
    try {
      const params = new URLSearchParams({
        limit: '50',
        linked: 'false' // Only show unlinked items
      });
      if (search) params.append('search', search);
      
      const res = await fetch(`${API_URL}/api/catalog/vendor-items?${params}`);
      if (res.ok) {
        const data = await res.json();
        setVendorItems(data.vendor_items || []);
      }
    } catch (error) {
      console.error('Error loading vendor items:', error);
    }
    setLoadingVendorItems(false);
  };

  // Open link modal
  const openLinkModal = () => {
    setShowLinkModal(true);
    setVendorSearch('');
    setSelectedVendorItem(null);
    loadVendorItems();
  };

  // Search vendor items
  const handleVendorSearch = (value) => {
    setVendorSearch(value);
    loadVendorItems(value);
  };

  // Link vendor item to product
  const linkVendorItem = async () => {
    if (!selectedVendorItem) return;
    
    setLinking(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/vendor-items/${selectedVendorItem.id}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId })
      });
      
      if (res.ok) {
        toast.success('Vendor item linked successfully');
        setShowLinkModal(false);
        loadProduct();
        onUpdate?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to link vendor item');
      }
    } catch (error) {
      toast.error('Error linking vendor item');
    }
    setLinking(false);
  };

  // Unlink vendor item from product
  const unlinkVendorItem = async () => {
    if (!vendorItem) return;
    
    setLinking(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/vendor-items/${vendorItem.id}/unlink`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (res.ok) {
        toast.success('Vendor item unlinked');
        setVendorItem(null);
        loadProduct();
        onUpdate?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to unlink vendor item');
      }
    } catch (error) {
      toast.error('Error unlinking vendor item');
    }
    setLinking(false);
  };

  // Recalculate prices (Phase 5)
  const recalculatePrices = async () => {
    if (!product?.preferred_vendor_item_id) {
      toast.error('Link a vendor item first to calculate prices');
      return;
    }
    
    setCalculating(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/pricing/calculate/${productId}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Prices calculated with ${data.markup_applied}% markup`);
        loadProduct();
        onUpdate?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to calculate prices');
      }
    } catch (error) {
      toast.error('Error calculating prices');
    }
    setCalculating(false);
  };

  // Calculate markup percentage
  const calculateMarkup = (cost, retail) => {
    if (!cost || cost === 0) return null;
    return ((retail - cost) / cost * 100).toFixed(0);
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="product-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Package className="w-5 h-5" />
              {loading ? 'Loading...' : product?.name || 'Product Details'}
              {product?.pricing_type && (
                <Badge className={`${PRICING_TYPE_BADGE[product.pricing_type]?.color || 'bg-gray-500'} text-white`}>
                  {PRICING_TYPE_BADGE[product.pricing_type]?.label || product.pricing_type}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : product ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
                <TabsTrigger value="variants">Variants ({product.variants?.length || 0})</TabsTrigger>
                <TabsTrigger value="vendor" data-testid="vendor-link-tab">
                  Vendor Link
                  {vendorItem && <Check className="w-3 h-3 ml-1 text-green-500" />}
                </TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4">
                <div className="flex justify-end">
                  {editMode ? (
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setEditMode(false)} disabled={saving}>
                        <X className="w-4 h-4 mr-2" /> Cancel
                      </Button>
                      <Button onClick={handleSave} disabled={saving}>
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setEditMode(true)}>
                      Edit
                    </Button>
                  )}
                </div>

                {/* Vendor Info Card - For Staff Ordering */}
                {vendorItem && (
                  <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <Truck className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-xs text-amber-600 font-medium uppercase tracking-wide">Order From Vendor</p>
                            <p className="font-bold text-lg text-amber-900">{vendorItem.vendor_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-amber-600 font-medium uppercase tracking-wide">Vendor SKU</p>
                          <p className="font-mono text-xl font-bold text-amber-900" data-testid="vendor-sku-display">{vendorItem.vendor_sku}</p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-amber-200 grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-amber-600">Cost:</span>
                          <span className="ml-1 font-bold">${vendorItem.cost_price?.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-amber-600">Min Qty:</span>
                          <span className="ml-1 font-medium">{vendorItem.min_order_qty || 1}</span>
                        </div>
                        <div>
                          <span className="text-amber-600">Lead Time:</span>
                          <span className="ml-1 font-medium">{vendorItem.lead_time_days || '—'} days</span>
                        </div>
                        <div>
                          <span className="text-amber-600">Type:</span>
                          <Badge variant="outline" className="ml-1 text-xs">{vendorItem.vendor_type}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-500 text-sm">Product Name</Label>
                      {editMode ? (
                        <Input
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          data-testid="product-detail-name"
                        />
                      ) : (
                        <p className="font-medium text-lg">{product.name}</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-gray-500 text-sm">SKU</Label>
                      {editMode ? (
                        <Input
                          value={formData.sku}
                          onChange={(e) => handleChange('sku', e.target.value)}
                        />
                      ) : (
                        <p className="font-mono">{product.sku || '—'}</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-gray-500 text-sm">Description</Label>
                      {editMode ? (
                        <Textarea
                          value={formData.description}
                          onChange={(e) => handleChange('description', e.target.value)}
                          rows={3}
                        />
                      ) : (
                        <p className="text-gray-700">{product.description || '—'}</p>
                      )}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-500 text-sm">Category</Label>
                        <p className="font-medium">{product.category_name || product.category_code || product.category_id || '—'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 text-sm">Product Type</Label>
                        <p className="font-medium">{product.product_type || '—'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-500 text-sm">Pricing Type</Label>
                        <Badge className={`${PRICING_TYPE_BADGE[product.pricing_type]?.color || 'bg-gray-500'} text-white`}>
                          {product.pricing_type || 'fixed'}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-gray-500 text-sm">Pricing Source</Label>
                        <Badge variant="outline">{product.pricing_source || 'manual'}</Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editMode ? formData.is_active : product.is_active}
                          onChange={(e) => handleChange('is_active', e.target.checked)}
                          disabled={!editMode}
                          className="rounded"
                        />
                        <Label>Active</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editMode ? formData.taxable : product.taxable}
                          onChange={(e) => handleChange('taxable', e.target.checked)}
                          disabled={!editMode}
                          className="rounded"
                        />
                        <Label>Taxable</Label>
                      </div>
                    </div>

                    {product.tags?.length > 0 && (
                      <div>
                        <Label className="text-gray-500 text-sm">Tags</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {product.tags.map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              <Tag className="w-3 h-3 mr-1" />
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timestamps */}
                <div className="pt-4 border-t text-xs text-gray-400">
                  <p>Created: {product.created_at ? new Date(product.created_at).toLocaleString() : '—'}</p>
                  <p>Updated: {product.updated_at ? new Date(product.updated_at).toLocaleString() : '—'}</p>
                  <p>ID: {product.id}</p>
                </div>
              </TabsContent>

              {/* Pricing Tab */}
              <TabsContent value="pricing" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4" /> Pricing Configuration
                      </span>
                      {vendorItem && (
                        <Button
                          size="sm"
                          onClick={recalculatePrices}
                          disabled={calculating}
                          data-testid="recalculate-prices-btn"
                        >
                          {calculating ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculating...</>
                          ) : (
                            <><Calculator className="w-4 h-4 mr-2" /> Recalculate Prices</>
                          )}
                        </Button>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label className="text-gray-500 text-sm">Pricing Type</Label>
                        <p className="font-medium">{product.pricing_type || 'fixed'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 text-sm">Pricing Source</Label>
                        <Badge variant={product.pricing_source === 'calculated' ? 'default' : 'outline'}>
                          {product.pricing_source || 'manual'}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-gray-500 text-sm">Base Price</Label>
                        <p className="font-medium text-lg">${(product.retail_price || 0).toFixed(2)}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 text-sm">Applied Markup</Label>
                        <p className="font-medium text-lg">
                          {product.markup_percent_applied ? `${product.markup_percent_applied}%` : '—'}
                        </p>
                      </div>
                    </div>

                    {/* Cost vs Retail Comparison (if vendor linked) */}
                    {vendorItem && (
                      <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                        <p className="font-medium mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-green-600" />
                          Cost vs Retail Analysis
                        </p>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500 block">Vendor Cost</span>
                            <span className="font-bold text-green-600 text-lg">${vendorItem.cost_price?.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-center">
                            <ArrowRight className="w-5 h-5 text-gray-400" />
                          </div>
                          <div>
                            <span className="text-gray-500 block">Retail Price</span>
                            <span className="font-bold text-blue-600 text-lg">${(product.retail_price || 0).toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 block">Markup</span>
                            <span className="font-bold text-purple-600 text-lg">
                              {calculateMarkup(vendorItem.cost_price, product.retail_price) || '—'}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Per Sqft Pricing */}
                    {product.pricing_type === 'per_sqft' && product.sqft_pricing && (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="font-medium mb-2">Per Square Foot Pricing</p>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Rate:</span>
                            <span className="ml-2 font-medium">${product.sqft_pricing.rate_per_sqft}/sqft</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Min:</span>
                            <span className="ml-2">{product.sqft_pricing.min_sqft} sqft</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Min Charge:</span>
                            <span className="ml-2">${product.sqft_pricing.min_charge}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Option Matrix Pricing */}
                    {product.pricing_type === 'option_matrix' && product.option_pricing && (
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <p className="font-medium mb-2">Option Matrix Pricing</p>
                        <div className="text-sm mb-2">
                          <span className="text-gray-500">Options:</span>
                          {product.option_pricing.options && Object.entries(product.option_pricing.options).map(([key, values]) => (
                            <span key={key} className="ml-2">
                              {key}: {Array.isArray(values) ? values.join(', ') : values}
                            </span>
                          ))}
                        </div>
                        {product.option_pricing.price_table && (
                          <div className="overflow-auto max-h-48">
                            <table className="text-xs w-full">
                              <thead>
                                <tr className="bg-purple-100">
                                  {Object.keys(product.option_pricing.price_table[0] || {}).map(k => (
                                    <th key={k} className="p-1 text-left">{k}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {product.option_pricing.price_table.slice(0, 10).map((row, i) => (
                                  <tr key={i} className="border-b">
                                    {Object.values(row).map((v, j) => (
                                      <td key={j} className="p-1">{typeof v === 'number' ? `$${v.toFixed(2)}` : v}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {product.option_pricing.price_table.length > 10 && (
                              <p className="text-xs text-gray-400 mt-1">
                                + {product.option_pricing.price_table.length - 10} more rows
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Variants Tab */}
              <TabsContent value="variants" className="space-y-4">
                {product.variants?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-3 text-left">Variant</th>
                          <th className="p-3 text-left">SKU</th>
                          <th className="p-3 text-left">Size</th>
                          <th className="p-3 text-left">Color</th>
                          <th className="p-3 text-right">Price</th>
                          <th className="p-3 text-center">Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.variants.map((v, i) => (
                          <tr key={i} className="border-t hover:bg-gray-50">
                            <td className="p-3 font-medium">{v.name || v.variant_name || `Variant ${i + 1}`}</td>
                            <td className="p-3 font-mono text-xs">{v.sku || v.variant_sku || '—'}</td>
                            <td className="p-3">{v.size || '—'}</td>
                            <td className="p-3">
                              {v.color ? (
                                <span className="flex items-center gap-2">
                                  <span 
                                    className="w-4 h-4 rounded border" 
                                    style={{ backgroundColor: v.color_hex || v.color }}
                                  />
                                  {v.color}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="p-3 text-right font-medium">
                              ${(v.price || v.retail_price || 0).toFixed(2)}
                            </td>
                            <td className="p-3 text-center">
                              {v.in_stock !== undefined ? (
                                <Badge variant={v.in_stock ? 'default' : 'secondary'}>
                                  {v.in_stock ? 'In Stock' : 'Out'}
                                </Badge>
                              ) : (
                                v.stock_quantity ?? '—'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No variants defined</p>
                    <p className="text-sm">This product uses {product.pricing_type || 'fixed'} pricing</p>
                  </div>
                )}
              </TabsContent>

              {/* Vendor Link Tab */}
              <TabsContent value="vendor" className="space-y-4" data-testid="vendor-link-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Truck className="w-4 h-4" /> Vendor Source
                      </span>
                      {vendorItem ? (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={unlinkVendorItem}
                          disabled={linking}
                          data-testid="unlink-vendor-btn"
                        >
                          {linking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlink className="w-4 h-4 mr-2" />}
                          Unlink
                        </Button>
                      ) : (
                        <Button 
                          size="sm" 
                          onClick={openLinkModal}
                          data-testid="link-vendor-btn"
                        >
                          <LinkIcon className="w-4 h-4 mr-2" />
                          Link Vendor Item
                        </Button>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {vendorItem ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <LinkIcon className="w-5 h-5 text-green-600" />
                          <div className="flex-1">
                            <p className="font-medium">Linked to Vendor Item</p>
                            <p className="text-sm text-gray-500">{vendorItem.vendor_name} - {vendorItem.vendor_sku}</p>
                          </div>
                          <Badge variant="default" className="bg-green-500">Active</Badge>
                        </div>

                        <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-500">Vendor SKU:</span>
                              <span className="ml-2 font-mono">{vendorItem.vendor_sku}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Vendor Item Name:</span>
                              <span className="ml-2">{vendorItem.name}</span>
                            </div>
                          </div>
                          
                          {/* Cost vs Retail */}
                          <div className="grid grid-cols-3 gap-3 pt-3 border-t">
                            <div className="text-center p-2 bg-white rounded">
                              <p className="text-xs text-gray-500">Cost Price</p>
                              <p className="font-bold text-green-600">${vendorItem.cost_price?.toFixed(2)}</p>
                            </div>
                            <div className="text-center p-2 bg-white rounded">
                              <p className="text-xs text-gray-500">Retail Price</p>
                              <p className="font-bold text-blue-600">${(product.retail_price || 0).toFixed(2)}</p>
                            </div>
                            <div className="text-center p-2 bg-white rounded">
                              <p className="text-xs text-gray-500">Markup</p>
                              <p className="font-bold text-purple-600">
                                {calculateMarkup(vendorItem.cost_price, product.retail_price) || '—'}%
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-sm pt-3 border-t">
                            <div>
                              <span className="text-gray-500">Vendor Variants:</span>
                              <span className="ml-2">{vendorItem.variants?.length || 0}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Vendor Type:</span>
                              <Badge variant="outline" className="ml-2">{vendorItem.vendor_type}</Badge>
                            </div>
                          </div>
                        </div>

                        <div className="text-xs text-gray-400">
                          <p>Last synced: {vendorItem.synced_at ? new Date(vendorItem.synced_at).toLocaleString() : 'Never'}</p>
                          <p>Vendor Item ID: {vendorItem.id}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No vendor link</p>
                        <p className="text-sm mt-2 mb-4">
                          Link a vendor item to enable cost tracking and auto-sync.
                        </p>
                        <Button onClick={openLinkModal} data-testid="link-vendor-btn-empty">
                          <LinkIcon className="w-4 h-4 mr-2" />
                          Link Vendor Item
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Product IDs */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Info className="w-4 h-4" /> Product IDs
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-gray-500">Product ID:</span>
                        <span className="ml-2 font-mono text-xs">{product.id}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Category ID:</span>
                        <span className="ml-2 font-mono text-xs">{product.category_id || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Preferred Vendor Item:</span>
                        <span className="ml-2 font-mono text-xs">{product.preferred_vendor_item_id || '—'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Product not found</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Item Link Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="link-vendor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5" />
              Link Vendor Item
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search vendor items by name or SKU..."
                value={vendorSearch}
                onChange={(e) => handleVendorSearch(e.target.value)}
                className="pl-10"
                data-testid="vendor-search-input"
              />
            </div>

            {/* Vendor Items List */}
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {loadingVendorItems ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : vendorItems.length > 0 ? (
                <div className="divide-y">
                  {vendorItems.map(item => (
                    <div
                      key={item.id}
                      className={`p-3 cursor-pointer transition-colors ${
                        selectedVendorItem?.id === item.id
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedVendorItem(item)}
                      data-testid={`vendor-item-option-${item.vendor_sku}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-gray-500">{item.vendor_sku}</span>
                            <Badge variant="outline" className="text-xs">{item.vendor_name || item.vendor_code}</Badge>
                          </div>
                          <p className="font-medium truncate">{item.name}</p>
                          {item.category_code && (
                            <Badge variant="secondary" className="text-xs mt-1">{item.category_code}</Badge>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-bold text-green-600">${item.cost_price?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{item.variant_count || 0} variants</p>
                        </div>
                        {selectedVendorItem?.id === item.id && (
                          <Check className="w-5 h-5 text-blue-500 ml-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No unlinked vendor items found</p>
                  {vendorSearch && <p className="text-sm">Try a different search term</p>}
                </div>
              )}
            </div>

            {/* Selected Item Preview */}
            {selectedVendorItem && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium mb-1">Selected:</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedVendorItem.name}</p>
                    <p className="text-sm text-gray-500">
                      {selectedVendorItem.vendor_name} • {selectedVendorItem.vendor_sku}
                    </p>
                  </div>
                  <p className="font-bold text-green-600">${selectedVendorItem.cost_price?.toFixed(2)}</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowLinkModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={linkVendorItem} 
                disabled={!selectedVendorItem || linking}
                data-testid="confirm-link-btn"
              >
                {linking ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Linking...</>
                ) : (
                  <><LinkIcon className="w-4 h-4 mr-2" /> Link to Product</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductDetailModal;
